// Bell dropdown — the in-app notification center (Gmail / Teams activity feed).
//
// Self-contained: renders the header bell (with an unread badge) and a Proton
// dropdown listing the notifications.
//
// Rows are grouped Unread / Earlier (the server already returns unread-first).
// Clicking a row marks it read and navigates to its in-app link; a header
// "Mark all read" flips everything at once.

import { useNavigate } from "react-router-dom";
import {
  Bell, Mail, Users, Video, AtSign, CalendarClock, FileText, Info, Check,
} from "lucide-react";
import clsx from "clsx";
import { Button } from "@/proton/ui/Button";
import { Icon } from "@/proton/ui/Icon";
import { Dropdown } from "@/proton/ui/Dropdown";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type AppNotification,
} from "@/hooks/useNotifications";

const KIND_ICON: Record<string, typeof Bell> = {
  mail: Mail,
  ticket: Users,
  meeting: Video,
  mention: AtSign,
  booking: CalendarClock,
  file: FileText,
  system: Info,
};

function iconFor(kind: string) {
  return KIND_ICON[kind] ?? KIND_ICON.system;
}

// Compact relative time ("now", "4m", "3h", "2d", else a date).
function relTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "now";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function NotificationCenter() {
  const navigate = useNavigate();

  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = data?.items ?? [];
  const unread = data?.unread_count ?? 0;
  const unreadItems = items.filter((n) => !n.read);
  const readItems = items.filter((n) => n.read);

  // Proton dropdown off a ghost header button; rows are dropdown items.
  return (
    <Dropdown
      placement="bottom-end"
      minWidth="22em"
      maxWidth="min(24rem, calc(100vw - 1.5rem))"
      maxHeight="min(70vh, 32rem)"
      trigger={({ ref, open, toggle }) => (
        <Button
          ref={ref}
          shape="ghost"
          color="weak"
          icon
          className="relative"
          title="Notifications"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
          aria-expanded={open}
          onClick={toggle}
        >
          <Icon as={Bell} />
          {unread > 0 && <span className="header-badge" aria-hidden="true">{unread > 99 ? "99+" : unread}</span>}
        </Button>
      )}
    >
      {(close) => {
        function activate(n: AppNotification) {
          if (!n.read) markRead.mutate(n.id);
          close();
          if (n.link) navigate(n.link);
        }
        return (
          <div>
            <div className="flex items-center gap-2 px-4 pt-3 pb-1">
              <span className="text-bold">Notifications</span>
              {unread > 0 && <span className="badge-label-primary">{unread} new</span>}
              <span className="flex-1" />
              <Button
                shape="ghost"
                color="norm"
                size="small"
                disabled={unread === 0 || markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                Mark all as read
              </Button>
            </div>

            {isLoading && <p className="m-0 px-4 py-6 text-center color-weak">Loading…</p>}
            {!isLoading && items.length === 0 && (
              <div className="px-4 py-8 text-center">
                <Icon as={Bell} size={6} className="color-hint mx-auto" />
                <p className="mt-2 mb-0 color-weak">You're all caught up</p>
              </div>
            )}

            <ul className="m-0 p-0 list-none pb-1">
              {unreadItems.length > 0 && <li className="dropdown-item-title" role="presentation">Unread</li>}
              {unreadItems.map((n) => (
                <Row key={n.id} n={n} onActivate={activate} onMarkRead={() => markRead.mutate(n.id)} />
              ))}
              {readItems.length > 0 && <li className="dropdown-item-title" role="presentation">Earlier</li>}
              {readItems.map((n) => (
                <Row key={n.id} n={n} onActivate={activate} />
              ))}
            </ul>
          </div>
        );
      }}
    </Dropdown>
  );
}

function Row({
  n,
  onActivate,
  onMarkRead,
}: {
  n: AppNotification;
  onActivate: (n: AppNotification) => void;
  onMarkRead?: () => void;
}) {
  return (
    <li className="dropdown-item notification-row">
      <div
        className="dropdown-item-button items-start"
        role="button"
        tabIndex={0}
        onClick={() => onActivate(n)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate(n);
          }
        }}
      >
        <Icon as={iconFor(n.kind)} className={clsx("mt-0.5", n.read ? "color-weak" : "color-primary")} />
        <span className="flex-1 min-w-0">
          <span className={clsx("block text-ellipsis", !n.read && "text-semibold")}>{n.title}</span>
          {n.body && <span className="block text-ellipsis text-sm color-weak">{n.body}</span>}
        </span>
        <span className="notification-row-time text-sm color-weak shrink-0 text-tabular">{relTime(n.created_at)}</span>
      </div>
      {!n.read && onMarkRead && (
        <Button
          shape="ghost"
          size="small"
          icon
          className="notification-row-read"
          title="Mark as read"
          aria-label="Mark as read"
          onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
        >
          <Icon as={Check} />
        </Button>
      )}
    </li>
  );
}
